import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  addOtherIncome,
  deleteOtherIncome,
  editOtherIncome,
  restoreOtherIncome,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { useGoBack } from '../../spending/hooks';
import { copy, errorText } from '../copy';
import { minorToInput, parsePesoInput } from '../format';
import { useAccounts, useBudgetSettings, useOtherIncome, useToday } from '../hooks';
import { AccountChips } from '../components/AccountChips';

interface Initial {
  amount: string;
  note: string;
  date: string;
  accountId: string | null | undefined;
}

/**
 * Spec: one-off quick adds (side job, refund, gift). With an id it edits or deletes that income
 * (Budget owns it, so it can change any time; a payday's income goes through the payday screen).
 */
export function OtherIncomeRoute() {
  const { id } = useParams();
  const today = useToday();
  const entry = useOtherIncome(id ?? '');
  const goBack = useGoBack('/m/budget/income');
  if (!id)
    return (
      <OtherIncomeForm initial={{ amount: '', note: '', date: today, accountId: undefined }} />
    );
  if (entry === undefined) return null;
  if (entry === null || entry.deletedAt) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editOtherIncome}>
        <p className="text-muted">{errorText('not_found')}</p>
      </Sheet>
    );
  }
  return (
    <OtherIncomeForm
      id={id}
      initial={{
        amount: minorToInput(entry.amountMinor),
        note: entry.note ?? '',
        date: entry.date,
        accountId: entry.accountId,
      }}
    />
  );
}

function OtherIncomeForm({ id, initial }: { id?: string; initial: Initial }) {
  const env = useCommandEnv();
  const navigate = useNavigate();
  const goBack = useGoBack('/m/budget/income');
  const today = useToday();
  const [amount, setAmount] = useState(initial.amount);
  const [note, setNote] = useState(initial.note);
  const [date, setDate] = useState(initial.date);
  const accounts = useAccounts() ?? [];
  const settings = useBudgetSettings();
  const [accountId, setAccountId] = useState<string | null | undefined>(initial.accountId);
  const pickedAccount = accountId === undefined ? (settings?.incomeAccountId ?? null) : accountId;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const minor = parsePesoInput(amount);

  async function save() {
    if (minor === null) return;
    setBusy(true);
    if (id) {
      const r = await editOtherIncome(env, id, {
        amountMinor: minor,
        note,
        date,
        ...(accountId !== undefined ? { accountId } : {}),
      });
      setBusy(false);
      if (!r.ok) {
        setError(errorText(r.error));
        return;
      }
      if (Object.keys(r.undo).length > 0) {
        showToast({
          message: copy.savedIncome,
          action: { label: copy.undo, onPress: () => void editOtherIncome(env, id, r.undo) },
        });
      }
      goBack();
      return;
    }
    const r = await addOtherIncome(env, {
      amountMinor: minor,
      note,
      date,
      accountId: pickedAccount,
    });
    setBusy(false);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    showToast({
      message: copy.addedIncome(minor),
      action: { label: copy.undo, onPress: () => void deleteOtherIncome(env, r.id) },
    });
    goBack();
  }

  async function remove() {
    if (!id) return;
    if (!armed) {
      setArmed(true);
      return;
    }
    const r = await deleteOtherIncome(env, id);
    if (!r.ok) {
      setError(errorText(r.error));
      return;
    }
    showToast({
      message: copy.deletedIncome(parsePesoInput(initial.amount) ?? 0),
      action: { label: copy.undo, onPress: () => void restoreOtherIncome(env, id) },
    });
    navigate('/m/budget/income', { replace: true });
  }

  const footer = (
    <Button variant="primary" block disabled={minor === null || busy} onClick={() => void save()}>
      {copy.save}
    </Button>
  );

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={id ? copy.editOtherIncome : copy.addOtherIncome}
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <TextField
          label={copy.amount}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          label={copy.notePlaceholder}
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
        <TextField
          label={copy.dateLabel}
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        {accounts.length > 0 ? (
          <AccountChips
            label={copy.landsIn}
            accounts={accounts}
            value={pickedAccount}
            onChange={setAccountId}
          />
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {id ? (
          <Button variant="destructive" block onClick={() => void remove()}>
            {armed ? copy.confirmDelete : copy.delete}
          </Button>
        ) : null}
      </div>
    </Sheet>
  );
}
